import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaRocket,
  FaShieldAlt,
  FaSignInAlt,
  FaSpinner,
  FaUsers,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { verifyUser } from "../../services/userServices";
import backgroundImage from "./bouscat02.webp";

const SHOW_DEMO_LOGIN = false;

const LoginPage = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const saveUserAndRedirect = (user) => {
    localStorage.setItem("user", JSON.stringify(user));

    if (user?.token) {
      localStorage.setItem("token", user.token);
    }

    navigate("/appli/messages");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast.error("Veuillez renseigner votre email et votre mot de passe.");
      return;
    }

    try {
      setLoading(true);

      const user = await verifyUser(formData.email, formData.password);

      if (user) {
        toast.success("Connexion réussie !");
        saveUserAndRedirect(user);
      } else {
        toast.error("Email ou mot de passe incorrect.");
      }
    } catch (error) {
      console.error("Erreur lors de la connexion :", error);
      toast.error("Erreur lors de la connexion. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    const demoCredentials = {
      email: "demo@gmail.com",
      password: "demo",
    };

    try {
      setDemoLoading(true);

      const user = await verifyUser(
        demoCredentials.email,
        demoCredentials.password
      );

      if (user) {
        toast.success("Connexion au mode démonstration réussie !");
        saveUserAndRedirect(user);
      } else {
        toast.error("Impossible de se connecter au mode démonstration.");
      }
    } catch (error) {
      console.error("Erreur lors de la connexion en mode démo :", error);
      toast.error("Erreur lors de la connexion en mode démo.");
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-slate-900 dark:text-white">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />

      <div className="absolute inset-0 bg-slate-950/75" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(34,197,94,0.20),transparent_35%),radial-gradient(circle_at_80%_35%,rgba(59,130,246,0.25),transparent_40%)]" />

      <button
        type="button"
        onClick={() => navigate("/")}
        className="absolute left-4 top-4 z-20 inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold text-white/90 backdrop-blur-md transition hover:bg-white hover:text-slate-950 sm:left-6 sm:top-6"
      >
        <FaArrowLeft />
        Retour au site
      </button>

      <main className="relative z-10 flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid w-full max-w-6xl overflow-hidden rounded-[2rem] border border-white/10 bg-white/95 shadow-2xl backdrop-blur-xl dark:bg-slate-900/95 lg:grid-cols-[1.05fr_0.95fr]">
          <section className="hidden bg-slate-950 p-10 text-white lg:flex lg:flex-col lg:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold text-white/90">
                <FaShieldAlt />
                Espace administration
              </span>

              <h1 className="mt-8 text-5xl font-bold leading-tight">
                Pilotez votre portail associatif simplement.
              </h1>

              <p className="mt-6 max-w-xl text-base leading-8 text-white/70">
                Connectez-vous pour gérer les associations, les actualités, les
                événements, les infrastructures, les responsables, les messages
                et les paramètres de votre site.
              </p>
            </div>

            <div className="mt-12 grid gap-4">
              <FeatureLine
                icon={FaUsers}
                title="Gestion des associations"
                text="Fiches, contacts, horaires, photos et contenus publics."
              />
              <FeatureLine
                icon={FaRocket}
                title="Interface rapide"
                text="Un espace pensé pour une administration simple au quotidien."
              />
              <FeatureLine
                icon={FaShieldAlt}
                title="Accès sécurisé"
                text="Connexion avec attribution des rôles par l’administrateur."
              />
            </div>
          </section>

          <section className="p-6 sm:p-8 lg:p-10">
            <div className="mx-auto flex max-w-md flex-col justify-center">
              <div className="text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-primary/10 text-primary">
                  <FaSignInAlt size={24} />
                </div>

                <h2 className="mt-6 text-3xl font-bold text-slate-950 dark:text-white">
                  Connexion
                </h2>

                <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-300">
                  Accédez à votre espace d’administration MonAppliAsso.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
                  >
                    Email
                  </label>

                  <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 transition focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/10 dark:border-white/10 dark:bg-slate-950">
                    <FaEnvelope className="shrink-0 text-slate-400" />
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="votre@email.fr"
                      autoComplete="email"
                      className="w-full bg-transparent text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400 dark:text-white"
                      required
                    />
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label
                      htmlFor="password"
                      className="block text-sm font-bold text-slate-700 dark:text-slate-200"
                    >
                      Mot de passe
                    </label>

                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="text-xs font-bold text-primary transition hover:opacity-80"
                    >
                      {showPassword ? "Masquer" : "Afficher"}
                    </button>
                  </div>

                  <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 transition focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/10 dark:border-white/10 dark:bg-slate-950">
                    <FaLock className="shrink-0 text-slate-400" />
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Votre mot de passe"
                      autoComplete="current-password"
                      className="w-full bg-transparent text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400 dark:text-white"
                      required
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="shrink-0 text-slate-400 transition hover:text-primary"
                      aria-label={
                        showPassword
                          ? "Masquer le mot de passe"
                          : "Afficher le mot de passe"
                      }
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || demoLoading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Connexion...
                    </>
                  ) : (
                    <>
                      <FaSignInAlt />
                      Se connecter
                    </>
                  )}
                </button>

                {SHOW_DEMO_LOGIN && (
                  <button
                    type="button"
                    onClick={handleDemoLogin}
                    disabled={loading || demoLoading}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-6 py-3.5 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:text-primary hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                  >
                    {demoLoading ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Connexion démo...
                      </>
                    ) : (
                      <>
                        <FaRocket />
                        Accéder au mode démonstration
                      </>
                    )}
                  </button>
                )}
              </form>

              <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center text-xs leading-6 text-slate-500 dark:border-white/10 dark:bg-slate-950 dark:text-slate-300">
                Accès réservé aux utilisateurs autorisés par l’administrateur.
                En cas de problème de connexion, contactez le responsable du site.
              </div>
            </div>
          </section>
        </div>
      </main>

      <ToastContainer
        position="top-right"
        autoClose={2500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        theme="colored"
      />
    </div>
  );
};

function FeatureLine({ icon: Icon, title, text }) {
  return (
    <div className="flex gap-4 rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-primary">
        <Icon />
      </span>

      <div>
        <h3 className="text-base font-bold text-white">{title}</h3>
        <p className="mt-1 text-sm leading-6 text-white/65">{text}</p>
      </div>
    </div>
  );
}

export default LoginPage;